import { cn } from '@wayste/shadcn/lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';

/**
 * Counter Component
 *
 * A small pill badge that displays a numeric count. Used inside filter buttons,
 * tabs, and other components to surface quantity at a glance.
 *
 * Five variants cover the common use cases:
 * - default   → inactive filter button, inactive tab
 * - filled    → active filter button (primary color)
 * - secondary → tab badge (tinted primary surface)
 * - alert     → notification or error count (destructive color)
 * - warning   → warning count (tinted amber surface, dark amber text)
 *
 * @example
 * ```tsx
 * import { Counter } from '@wayste/shadcn/components/counter';
 *
 * // Inactive state (filter button, tab)
 * <Counter value={12} />
 *
 * // Active filter — filled primary
 * <Counter value={3} variant="filled" />
 *
 * // Tab badge — tinted surface
 * <Counter value={38} variant="secondary" />
 *
 * // Notification / error count
 * <Counter value={5} variant="alert" />
 *
 * // Warning count
 * <Counter value={12} variant="warning" />
 *
 * // Small size
 * <Counter value={3} variant="filled" size="sm" />
 *
 * // Overflow cap — renders "99+"
 * <Counter value={142} variant="filled" />
 * ```
 */

// ─── Variants ─────────────────────────────────────────────────────────────────

const counterVariants = cva('inline-flex items-center justify-center rounded-full font-medium tabular-nums whitespace-nowrap shrink-0', {
    variants: {
        /**
         * Visual style of the counter.
         * - default   — neutral surface, secondary text (inactive)
         * - filled    — solid primary background, white text (active filter)
         * - secondary — tinted primary surface, primary text (tab badge)
         * - alert     — solid destructive background, white text (errors/notifications)
         * - warning   — tinted warning surface, dark amber text (caution counts)
         */
        variant: {
            default: 'bg-surface-secondary text-text-secondary',
            filled: 'bg-primary-default text-primary-on-default-text',
            secondary: 'bg-primary-surface-default text-primary-on-surface-text',
            alert: 'bg-destructive-default text-destructive-on-default-text',
            warning: 'bg-warning-surface-default text-warning-on-surface-text',
        },
        /**
         * Height of the pill.
         * - default — 20px
         * - sm      — 16px
         */
        size: {
            default: 'h-5 min-w-5 px-1.5 text-[11px] leading-4',
            sm: 'h-4 min-w-4 px-1.5 text-[11px] leading-4',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});

// ─── Types ────────────────────────────────────────────────────────────────────

export type CounterVariant = NonNullable<VariantProps<typeof counterVariants>['variant']>;
export type CounterSize = NonNullable<VariantProps<typeof counterVariants>['size']>;

export interface CounterProps extends VariantProps<typeof counterVariants> {
    /** The numeric count to display. */
    value: number;
    /**
     * Cap above which the display becomes "{max}+".
     * @default 99
     */
    max?: number;
    className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const Counter = ({ value, max = 99, variant, size, className }: CounterProps) => {
    const display = value > max ? `${max}+` : String(value);
    return <span className={cn(counterVariants({ variant, size }), className)}>{display}</span>;
};
Counter.displayName = 'Counter';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Counter, counterVariants };
